import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Check, X, Pencil, Archive, RefreshCw, Brain, ShieldCheck } from 'lucide-react';
import { regenApi } from '../lib/api';

/**
 * What the team has learned — and from whom.
 *
 * The people who gave the feedback are the fastest at spotting a lesson that came out wrong, so
 * every stored rule is visible here with its provenance, and can be reworded, approved or retired.
 * `proposed` lessons are NOT injected into any prompt until someone approves them.
 */

type Lesson = {
  id: string; persona: string | null; category: string | null; rule: string;
  raw_feedback: string | null; source: string; status: string; hits: number;
  vertical: string | null; context: Record<string, any> | null; normalized: boolean;
  confirmed_by: string | null; created_at: string | null;
};

const STATUS_STYLE: Record<string, string> = {
  active: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  proposed: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  retired: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};

export function LessonReviewPanel({ personaFilter }: { personaFilter?: string }) {
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string>('');
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState('');
  const [mode, setMode] = useState<any>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await regenApi.teamLessonsAdmin({
        persona: personaFilter || undefined, status: status || undefined,
      });
      setLessons(r.data?.lessons || []); setSelected(new Set()); setErr('');
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Could not load what the team has learned');
    } finally { setLoading(false); }
  }, [personaFilter, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    regenApi.teamLearningStatus().then(r => setMode(r.data)).catch(() => { /* optional */ });
  }, [lessons.length]);

  const apply = async (id: string, next: 'active' | 'retired' | 'proposed', rule?: string) => {
    setBusy(id);
    try {
      await regenApi.teamLessonStatus(id, { status: next, rule });
      setEditing(null); await load();
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Could not update that lesson');
    } finally { setBusy(null); }
  };

  const proposed = lessons.filter(l => l.status === 'proposed').length;
  const selectable = lessons.filter(l => !l.confirmed_by && l.status !== 'retired');
  const allSelected = selectable.length > 0 && selectable.every(l => selected.has(l.id));

  const toggle = (id: string) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map(l => l.id)));

  const bulk = async (status: 'active' | 'retired') => {
    if (!selected.size) return;
    setBulkBusy(true);
    try {
      await regenApi.teamLessonsBulkStatus(Array.from(selected), status);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Could not apply that to the selected rules');
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border p-3 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Brain className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold text-gray-200">What the team has learned</h3>
        {proposed > 0 && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
            {proposed} awaiting your approval
          </span>
        )}
        <div className="flex-1" />
        {['', 'active', 'proposed', 'retired'].map(s => (
          <button key={s || 'all'} onClick={() => setStatus(s)}
            className={`text-[11px] px-2 py-1 rounded-lg border transition ${
              status === s ? 'bg-primary text-white border-primary' : 'border-border text-gray-400 hover:bg-white/5'}`}>
            {s || 'all'}
          </button>
        ))}
        <button onClick={load} className="p-1.5 rounded-lg border border-border text-gray-400 hover:bg-white/5">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Mode banner — the single answer to "is anything changing without me?" */}
      {mode && (
        <div className={`rounded-lg border p-2 text-[11px] flex items-start gap-2 ${
          mode.auto_apply
            ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
            : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'}`}>
          <ShieldCheck className="w-3.5 h-3.5 mt-px shrink-0" />
          {mode.auto_apply ? (
            <span><strong>Automatic mode.</strong> The system is applying what it infers on its own.</span>
          ) : (
            <span>
              <strong>Manual mode — nothing is applied unless you approve it.</strong>{' '}
              {mode.applied_now ?? 0} approved and in use · {mode.awaiting_review ?? 0} waiting for your review.
              Repetition raises a lesson's evidence but never applies it.
            </span>
          )}
        </div>
      )}

      <p className="text-[11px] text-gray-500">
        Each entry shows what a person actually said and the rule we intend to store, so you can
        check our reading against yours before it ever reaches the team.
      </p>

      {/* Bulk apply — tick the rules that look right and apply them together */}
      {selectable.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap rounded-lg border border-border bg-black/20 px-2 py-1.5">
          <label className="inline-flex items-center gap-1.5 text-[11px] text-gray-300 cursor-pointer">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-emerald-500" />
            Select all {selectable.length} not yet applied
          </label>
          <div className="flex-1" />
          <span className="text-[11px] text-gray-500">{selected.size} selected</span>
          <button disabled={!selected.size || bulkBusy} onClick={() => bulk('active')}
            className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg bg-emerald-600 text-white disabled:opacity-40">
            {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Apply selected
          </button>
          <button disabled={!selected.size || bulkBusy} onClick={() => bulk('retired')}
            className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg border border-border text-gray-300 disabled:opacity-40">
            <Archive className="w-3 h-3" />Retire selected
          </button>
        </div>
      )}

      {err && <p className="text-[11px] text-red-400">{err}</p>}
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-400 py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />loading…
        </div>
      ) : lessons.length === 0 ? (
        <p className="text-xs text-gray-500 py-6 text-center">Nothing learned yet.</p>
      ) : (
        <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
          {lessons.map(l => (
            <div key={l.id} className="rounded-lg border border-border p-2.5 bg-black/20">
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                {!l.confirmed_by && l.status !== 'retired' && (
                  <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggle(l.id)}
                    title="Select to apply in bulk" className="accent-emerald-500" />
                )}
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                  l.confirmed_by ? STATUS_STYLE.active : STATUS_STYLE[l.status] || STATUS_STYLE.retired}`}>
                  {l.status === 'retired' ? 'retired' : l.confirmed_by ? 'in use' : 'not applied'}
                </span>
                {l.persona && <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-gray-300">{l.persona}</span>}
                {l.category && <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-gray-400">{l.category}</span>}
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                  l.source === 'human' ? 'bg-blue-500/15 text-blue-300' : 'bg-white/5 text-gray-500'}`}>
                  {l.source === 'human' ? 'from a person' : 'auto-detected'}
                </span>
                {l.hits > 1 && <span className="text-[10px] text-gray-500">seen {l.hits}×</span>}
                {l.normalized && <span className="text-[10px] text-gray-500" title="Reworded into a reusable rule">reworded</span>}
              </div>

              {editing === l.id ? (
                <div className="flex gap-1.5">
                  <input value={draft} onChange={e => setDraft(e.target.value)}
                    className="flex-1 min-w-0 text-xs px-2 py-1.5 rounded-lg bg-black/30 border border-border text-gray-100" />
                  <button disabled={!draft.trim()} onClick={() => apply(l.id, 'active', draft.trim())}
                    className="text-[11px] px-2 py-1 rounded-lg bg-primary text-white disabled:opacity-50">Save &amp; approve</button>
                  <button onClick={() => setEditing(null)} className="text-gray-500 hover:text-gray-300 p-1"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <p className="text-xs text-gray-100">{l.rule}</p>
              )}

              {l.raw_feedback && l.raw_feedback !== l.rule && (
                <p className="mt-1 text-[10px] text-gray-500 italic">originally: “{l.raw_feedback}”</p>
              )}
              {l.context && Object.keys(l.context).length > 0 && (
                <p className="mt-0.5 text-[10px] text-gray-500">
                  applies when {Object.entries(l.context).map(([k, v]) => `${k}=${v}`).join(', ')}
                </p>
              )}

              {editing !== l.id && (
                <div className="mt-1.5 flex items-center gap-1.5">
                  {l.status !== 'active' && (
                    <button disabled={busy === l.id} onClick={() => apply(l.id, 'active')}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
                      {busy === l.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}Approve
                    </button>
                  )}
                  <button onClick={() => { setEditing(l.id); setDraft(l.rule); }}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border border-border text-gray-300 hover:bg-white/5">
                    <Pencil className="w-3 h-3" />Reword
                  </button>
                  {l.status !== 'retired' && (
                    <button disabled={busy === l.id} onClick={() => apply(l.id, 'retired')}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border border-border text-gray-400 hover:bg-white/5">
                      <Archive className="w-3 h-3" />Retire
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default LessonReviewPanel;
